import api from './api';
import { apiFetch, API_BASE_URL } from './api';
import { ensureFreshToken } from './tokenManager';

/**
 * AI Assistant Service
 * Handles AI-powered features and assistance
 */
export const aiService = {
  // ==================== AI Query ====================
  
  /**
   * Send AI query
   */
  async query(queryData) {
    return api.post('/ai-assistant/query', queryData);
  },

  /**
   * Get conversation history
   */
  async getConversationHistory(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/ai-assistant/conversation/history?${queryString}`);
  },

  /**
   * Clear conversation history
   */
  async clearConversationHistory() {
    return api.delete('/quick-actions/conversation/history');
  },

  // ==================== Portfolio Analysis ====================
  
  /**
   * Analyze portfolio
   */
  async analyzePortfolio(portfolioData) {
    return api.post('/ai-assistant/portfolio/analyze', portfolioData);
  },

  /**
   * Get portfolio health score
   */
  async getPortfolioHealthScore(portfolioId) {
    return api.get(`/ai-assistant/portfolio/${portfolioId}/health-score`);
  },

  /**
   * Get diversification analysis
   */
  async getDiversificationAnalysis(portfolioId) {
    return api.get(`/ai-assistant/portfolio/${portfolioId}/diversification`);
  },

  /**
   * Get risk analysis
   */
  async getRiskAnalysis(portfolioId) {
    return api.get(`/ai-assistant/portfolio/${portfolioId}/risk`);
  },

  // ==================== Market Insights ====================
  
  /**
   * Get market insights
   */
  async getMarketInsights(params) {
    return api.post('/ai-assistant/market/insights', params);
  },

  /**
   * Get trending stocks
   */
  async getTrendingStocks() {
    return api.get('/ai-assistant/market/trending');
  },

  /**
   * Get market movers
   */
  async getMarketMovers() {
    return api.get('/ai-assistant/market/movers');
  },

  /**
   * Get sector performance
   */
  async getSectorPerformance() {
    return api.get('/ai-assistant/market/sectors');
  },

  // ==================== Research Assistance ====================
  
  /**
   * Get research assistance
   */
  async getResearchAssistance(requestData) {
    return api.post('/ai-assistant/research/assist', requestData);
  },

  /**
   * Analyze security
   */
  async analyzeSecurity(symbol) {
    return api.get(`/ai-assistant/research/security/${symbol}`);
  },

  // ==================== Financial Planning ====================
  
  /**
   * Get financial planning assistance
   */
  async getFinancialPlanning(planningData) {
    return api.post('/ai-assistant/planning/assist', planningData);
  },

  /**
   * Run scenario analysis
   */
  async runScenarioAnalysis(scenarioData) {
    return api.post('/ai-assistant/planning/scenarios', scenarioData);
  },

  // ==================== AI Recommendations ====================
  
  /**
   * Get AI recommendations
   */
  async getRecommendations(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/ai-assistant/recommendations?${queryString}`);
  },

  // ==================== AI Feedback ====================
  
  /**
   * Provide feedback on AI response
   */
  async provideFeedback(feedbackData) {
    return api.post('/ai-assistant/feedback', feedbackData);
  },

  // ==================== AI Alerts ====================
  
  /**
   * Get AI-generated alerts
   */
  async getAlerts(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/ai-assistant/alerts?${queryString}`);
  },

  /**
   * Acknowledge alert
   */
  async acknowledgeAlert(alertId) {
    return api.post(`/ai-assistant/alerts/${alertId}/acknowledge`);
  },

  /**
   * Dismiss alert
   */
  async dismissAlert(alertId) {
    return api.delete(`/ai-assistant/alerts/${alertId}`);
  },

  /**
   * Get AI-suggested tasks
   */
  async getAITasks({ userId, clientId='', priority, maxResults }) {
    const params = new URLSearchParams();
    if (clientId) params.append('clientId', clientId);
    if (priority) params.append('priority', priority);
    if (maxResults) params.append('maxResults', maxResults.toString());
    return api.get(`/ai/tasks/${userId}?${params.toString()}`);
  },

  /**
   * Update task status (complete/cancel)
   */
  async updateTaskStatus(taskId, status, comment) {
    return api.patch(`/ai/tasks/${taskId}/status`, { status, comment });
  },

  /**
   * Get AI insights
   */
  async getAIInsights({ userId,clientId='', type, maxResults }) {
    const params = new URLSearchParams();
    if (clientId) params.append('clientId', clientId);
    if (type) params.append('type', type);
    if (maxResults) params.append('maxResults', maxResults.toString());
    return api.get(`/ai/insights/${userId}?${params.toString()}`);
  },

  /**
   * Get AI quick actions with latest history
   */
  async getAIQuickActionsWithLatestHistory(userId, clientId = null) {
    const params = new URLSearchParams({ userId });
    params.append('clientId', clientId);
    return api.get(`/ai/quick-actions/quick-actions-with-history?${params.toString()}`);
  },

  /**
   * Execute quick action update
   */
  async executeQuickActionUpdate(quickActionData) {
    return api.post('/ai/quick-actions/execute-update', quickActionData);
  },

  /**
   * Generate S3 presigned URL for download
   */
  async generateS3PresignedUrl(reportUrl) {
    return api.post('/ai/quick-actions/generate-presigned-url', { reportUrl });
  },

  /**
   * Create a new AI chat session
   */
  async createChatSession(sessionData) {
    return api.post('/ai/chat/sessions', sessionData);
  },

  /**
   * Save a chat message to a session
   */
  async saveChatMessage(messageData) {
    return api.post('/ai/chat/messages', messageData);
  },

  // ==================== AI Agents ====================

  /**
   * Send a message to the agent orchestrator (auto-routes to the domain agent)
   */
  async agentChat(payload) {
    return api.post('/ai/agent/chat', payload);
  },

  /**
   * Invoke a specific domain agent
   */
  async invokeAgent(domain, payload) {
    return api.post(`/ai/agent/${domain}/invoke`, payload);
  },

  /**
   * Generate a PDF report from an existing agent result
   */
  async agentCreateReport(domain, payload) {
    return api.post(`/ai/agent/${domain}/report`, payload);
  },

  /**
   * List generated agent reports for a client (or current user).
   */
  async getAgentReports(clientId) {
    return api.get(`/ai/agent/reports${clientId ? `?clientId=${clientId}` : ''}`);
  },

  /**
   * Delete an agent report: removes the DB record and the PDF file from S3.
   */
  async deleteAgentReport(id) {
    return api.delete(`/ai/agent/reports/${id}`);
  },

  // ==================== Quick Action Scheduling ====================

  async getQuickActionSchedules(userId) {
    return api.get(`/ai/quick-actions/schedules/user/${userId}`);
  },

  async getQuickActionSchedulesByClient(userId, clientId) {
    return api.get(`/ai/quick-actions/schedules/user/${userId}/client/${clientId}`);
  },

  async createQuickActionSchedule(schedule) {
    return api.post('/ai/quick-actions/schedules', schedule);
  },

  async toggleQuickActionSchedule(id, isActive) {
    return api.patch(`/ai/quick-actions/schedules/${id}/active`, isActive);
  },

  async deleteQuickActionSchedule(id) {
    return api.delete(`/ai/quick-actions/schedules/${id}`);
  },

  async runQuickActionSchedule(id) {
    return api.post(`/ai/quick-actions/schedules/${id}/run-now`);
  },

  // ==================== Client Financial Intake ====================

  async getFinancialIntake(clientId) {
    return api.get(`/ai/intake/${clientId}`);
  },

  async saveFinancialIntake(clientId, data) {
    return api.put(`/ai/intake/${clientId}`, data);
  },

  /**
   * Upload a financial statement/document to the agent corpus (client-scoped).
   */
  async uploadCorpusDocument(formData) {
    return apiFetch('/ai/corpus/upload', { method: 'POST', body: formData });
  },

  /**
   * Ingest a document already uploaded directly to S3 into the agent corpus.
   */
  async ingestCorpusS3(payload) {
    return api.post('/ai/corpus/ingest-s3', payload);
  },

  /**
   * List documents uploaded to the agent corpus (optionally for a client).
   */
  async getCorpusDocuments(clientId) {
    return api.get(`/ai/corpus/documents${clientId ? `?clientId=${clientId}` : ''}`);
  },

  /**
   * Get a short-lived presigned URL to view/download an uploaded corpus document.
   */
  async getCorpusDocumentDownloadUrl(documentId) {
    return api.get(`/ai/corpus/documents/${documentId}/download`);
  },

  // ==================== Sample Financial Intake ====================

  /**
   * Fetch the sample Financial Intake data (JSON) to prefill the form.
   */
  async getSampleIntake() {
    return api.get('/samples/financial-intake/data');
  },

  /**
   * Download the zip of all sample Financial Intake documents.
   */
  async downloadSampleDocuments() {
    const token = await ensureFreshToken();
    const resp = await fetch(`${API_BASE_URL}/samples/financial-intake/documents`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'financial-intake-sample-documents.zip';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Get persisted agent plans for a user
   */
  async getAgentPlans(userId) {
    return api.get(`/ai/agent/plans/${userId}`);
  },
};

export default aiService;
